import { useEffect, useState } from 'react';
import axios from 'axios';
import { contractorAuth, contractorToken } from './contractorAuth';
import { storedViewAsContractorId, storedViewAsUserId } from './viewAs';

/**
 * Which features this person may use (migration 078, Settings -> Features).
 *
 * GET /features/me answers { links, todos, meetings, recording, ai } for
 * whoever is asking - a member of staff, an administrator (as whoever they
 * are viewing as), or a contractor on a portal token. Asked once and shared,
 * the way lib/me.js shares who you are, and asked again:
 *
 *   - when the window comes back into focus, at most once a minute, so a
 *     switch an administrator moved is seen on the next look at the tab;
 *   - every three minutes while the tab is open;
 *   - at once, whenever any call is refused with the switch's 403 - the
 *     server is the authority, and the screen should catch up with it the
 *     moment it says so rather than three minutes later;
 *   - and when who is asking changes (View as, signing in or out).
 *
 * Until the first answer the features are UNKNOWN (null) and every feature
 * reads as on: with every switch at its default - the state of every
 * database until somebody changes one - that is the right answer, and the
 * bar does not flicker items in for the 99% of page loads where nothing is
 * off. A page behind a switch (components/FeatureRoute) waits for the
 * answer instead, so a switched-off page never starts loading.
 *
 * If the server cannot be asked at all, everything reads as on: the server
 * still refuses whatever it refuses, and a network blip must not hide the
 * firm's to-do lists. Like lib/me.js this only decides what is SHOWN.
 */
const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

export const FEATURE_KEYS = ['links', 'todos', 'meetings', 'recording', 'ai', 'docs', 'help', 'licences', 'contracts', 'gdpr', 'catalogue'];
// Licences (119), Contracts (120) and the GDPR tools (122) are
// administrators' by default; "on" here only means the door is not hidden by
// the switch - the screens are an administrator's anyway.
// Help (123, its own switch since 3 November) is staff's by default; a
// contractor's answer from the server says false.
export const ALL_ON = Object.freeze({ links: true, todos: true, meetings: true, recording: true, ai: true, docs: true, help: true, licences: true, contracts: true, gdpr: true, catalogue: true });

/** The names the screens use, the same words as the server's refusal. */
export const FEATURE_LABEL = {
  links: 'Links',
  todos: 'To do',
  meetings: 'Meetings',
  recording: 'Call recording & transcription',
  ai: 'AI summaries & minutes',
  docs: 'Documentation',
  help: 'Help',
  licences: 'Licences',
  contracts: 'Contracts',
  gdpr: 'GDPR tools',
  catalogue: 'Request something'
};

const REFRESH_MS = 3 * 60 * 1000;
const FOCUS_MIN_MS = 60 * 1000;

let cached = null;          // { features, ready, who, at, for }
let inflight = null;
const listeners = new Set();

/** Who is asking, as far as this browser knows - a change means ask again. */
const askingAs = () => {
  const token = contractorToken();
  if (token) return `t:${token.slice(-12)}`;
  return `s:${storedViewAsUserId() || ''}:${storedViewAsContractorId() || ''}`;
};

const tell = (value) => listeners.forEach((fn) => { try { fn(value); } catch { /* a listener must not break the rest */ } });

export function fetchFeatures({ force = false } = {}) {
  const who = askingAs();
  if (!force && cached && cached.for === who) return Promise.resolve(cached);
  if (!force && inflight) return inflight;
  // An administrator looking at a contractor's portal is answered for that
  // contractor (as=c:<id>), as the to-dos and Links are; staff View as is
  // already in the X-View-As-User header the interceptor adds.
  const viewing = !contractorToken() && storedViewAsContractorId();
  const url = `${API_BASE}/features/me${viewing ? `?as=c:${encodeURIComponent(viewing)}` : ''}`;
  inflight = axios.get(url, contractorAuth())
    .then((r) => ({
      features: { ...ALL_ON, ...(r.data?.features || {}) },
      ready: r.data?.ready !== false,
      who: r.data?.who || null,
      changed: Array.isArray(r.data?.changed) ? r.data.changed : []
    }))
    .catch(() => (cached && cached.for === who ? cached : { features: { ...ALL_ON }, ready: false, who: null, changed: [], failed: true }))
    .then((value) => {
      cached = { ...value, at: Date.now(), for: who };
      inflight = null;
      tell(cached);
      return cached;
    });
  return inflight;
}

export const refreshFeatures = () => fetchFeatures({ force: true });

/** Forget, for sign-out: the next person to use this tab is asked about afresh. */
export function forgetFeatures() {
  cached = null;
  inflight = null;
}

/* The three triggers to ask again, installed once, by the first hook. */
let installed = false;
function install() {
  if (installed || typeof window === 'undefined') return;
  installed = true;
  const onFocus = () => {
    if (!cached || Date.now() - cached.at >= FOCUS_MIN_MS) refreshFeatures();
  };
  window.addEventListener('focus', onFocus);
  document.addEventListener('visibilitychange', () => { if (document.visibilityState === 'visible') onFocus(); });
  setInterval(() => { if (listeners.size) refreshFeatures(); }, REFRESH_MS);
  axios.interceptors.response.use((r) => r, (error) => {
    if (error?.response?.status === 403 && error.response.data?.switched_off === true) refreshFeatures();
    return Promise.reject(error);
  });
}

/**
 * Every feature: { features, ready, who, changed } once known, null before
 * the first answer. Re-renders when the answer changes.
 */
export function useFeatures() {
  const [value, setValue] = useState(cached && cached.for === askingAs() ? cached : null);
  useEffect(() => {
    install();
    let live = true;
    const on = (v) => { if (live) setValue(v); };
    listeners.add(on);
    fetchFeatures().then(on);
    return () => { live = false; listeners.delete(on); };
  }, []);
  // A change of identity while mounted (View as switched): ask again.
  useEffect(() => {
    if (value && value.for !== askingAs()) fetchFeatures();
  });
  return value;
}

/**
 * May I use this feature? True until the server has said otherwise (see the
 * note at the top), so a door is drawn for the common case at once.
 */
export function useFeature(key) {
  const value = useFeatures();
  return value ? value.features[key] !== false : true;
}

/** The same question outside React, from whatever is cached. */
export const featureOn = (key) => (cached ? cached.features[key] !== false : true);
